<template>
  <div class="project-entry-actions" :class="{ 'has-pitch-deck': projectId === 'cathayflex' }" @keydown.stop>
    <component v-if="destination" :is="isExternalDestination ? 'a' : 'router-link'"
      v-bind="isExternalDestination ? { href: destination, target: '_blank', rel: 'noopener' } : { to: destination }"
      class="resource-action project-entry-primary project-detail-link">
      <span>Explore the project</span>
      <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 10h14m-5-5 5 5-5 5" /></svg>
    </component>
    <CathayFlexDeck v-if="projectId === 'cathayflex'" />
    <div class="project-entry-secondary">
      <GitHubRepoLink v-for="link in resources" :key="link.url" :href="link.url" :label="link.label" :icon-class="link.iconClass || 'fab fa-github'" compact />
      <button v-if="projectId === 'searchstory'" type="button" class="resource-action compose-view-entry" @click="openView"><span>Compose a <AiViewTerm /></span></button>
      <GalleryEntryLink v-if="projectId === 'searchstory'" />
      <ProjectArchitecture v-else-if="isSlidePoise" :project-id="projectId" />
    </div>
  </div>
</template>

<script>
import GitHubRepoLink from './GitHubRepoLink.vue';
import ProjectArchitecture from './ProjectArchitecture.vue';
import GalleryEntryLink from './GalleryEntryLink.vue';
import AiViewTerm from './AiViewTerm.vue';
import CathayFlexDeck from './CathayFlexDeck.vue';
import { projectById, projectResourceLinks } from '../../data/projects';
import { projectWebsiteTarget } from '../../utils/viewSourceTarget';
import '../../assets/css/resource-action.css';

export default {
  name: 'ProjectEntryActions',
  components: { GitHubRepoLink, ProjectArchitecture, GalleryEntryLink, AiViewTerm, CathayFlexDeck },
  props: { projectId: { type: String, required: true } },
  computed: {
    isSlidePoise() { return this.projectId === 'slidepoise'; },
    isExternalDestination() { return /^https?:\/\//.test(this.destination); },
    destination() {
      const route = projectById(this.projectId)?.route;
      return route && route !== '/projects' ? (projectWebsiteTarget(route)?.href || route) : '';
    },
    resources() { return projectResourceLinks[this.projectId] || []; }
  },
  methods: { openView() { window.dispatchEvent(new CustomEvent('open-site-view')); } },
};
</script>

<style>
.project-entry-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 32px; margin-top: 12px; }
.project-entry-actions.has-pitch-deck { display: grid; grid-template-columns: 1fr auto; gap: 0 32px; }
.project-entry-actions.has-pitch-deck > .cathay-deck-entry { grid-column: 1; grid-row: 2; }
.project-entry-actions.has-pitch-deck > .project-entry-secondary { grid-column: 2; grid-row: 1; }
.project-entry-actions .resource-action { height: 44px; min-height: 44px; padding: 0; font-size: 13px; line-height: 20px; }
.project-entry-actions .project-entry-primary { --entry-hover-ink: #191817; --entry-highlight: rgb(139 122 86 / 13%); margin-right: auto; color: #4f4d49; }
.project-entry-primary > span { text-decoration: underline; text-decoration-color: rgb(128 128 120 / 45%); text-underline-offset: 5px; text-decoration-thickness: 1px; background-image: linear-gradient(var(--entry-highlight), var(--entry-highlight)); background-position: 0 100%; background-repeat: no-repeat; background-size: 100% 0; transition: background-size 180ms cubic-bezier(.23, 1, .32, 1); }
.project-entry-primary svg { width: 17px; height: 17px; fill: none; stroke: currentColor; stroke-width: 1.25; stroke-linecap: round; stroke-linejoin: round; transition: transform 180ms cubic-bezier(.23, 1, .32, 1); }
.project-entry-actions .project-entry-primary::before { display: none; }
.project-entry-primary:focus-visible { color: var(--entry-hover-ink); }
.project-entry-primary:focus-visible > span { background-size: 100% .5em; }
.project-entry-primary:focus-visible svg { transform: translateX(3px); }
body.dark-mode .project-entry-actions .project-entry-primary { --entry-hover-ink: #f4f1ec; --entry-highlight: rgb(214 195 155 / 16%); color: #cfcbc5; }
@media (hover: hover) and (pointer: fine) {
  .project-entry-actions .project-entry-primary:hover,
  .timeline-event:hover:not(:has(a:hover, button:hover)) .project-entry-primary,
  .collection-detail-navigable:hover:not(:has(a:hover, button:hover)) .project-entry-primary { color: var(--entry-hover-ink); }
  .project-entry-primary:hover > span,
  .timeline-event:hover:not(:has(a:hover, button:hover)) .project-entry-primary > span,
  .collection-detail-navigable:hover:not(:has(a:hover, button:hover)) .project-entry-primary > span { background-size: 100% .5em; }
  .project-entry-primary:hover svg,
  .timeline-event:hover:not(:has(a:hover, button:hover)) .project-entry-primary svg,
  .collection-detail-navigable:hover:not(:has(a:hover, button:hover)) .project-entry-primary svg { transform: translateX(3px); }
}
.project-entry-secondary { display: flex; flex-wrap: wrap; align-items: center; gap: 0 28px; }
.project-entry-secondary > .project-architecture { display: flex; }
.project-entry-actions .repo-link-icon { font-size: 16px; }
@media (max-width: 600px) {
  .project-entry-actions.has-pitch-deck { grid-template-columns: 1fr; }
  .project-entry-actions.has-pitch-deck > .project-entry-secondary { grid-column: 1; grid-row: 3; }
  .project-entry-actions { display: grid; gap: 0; }
  .project-entry-secondary { gap: 0 24px; }
}
@media (prefers-reduced-motion: reduce) { .project-entry-primary > span, .project-entry-primary svg { transition: none; }.project-entry-primary:focus-visible svg { transform: none; } }
</style>
